import type { ListingApplicationPayload } from '#/api/ats';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type {
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from '#/api/types/ApiTypedId';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';

import { useListingApplicationSubmissionAsGuest } from './useListingApplicationSubmissionAsGuest';
import { useListingApplicationSubmissionAsUser } from './useListingApplicationSubmissionAsUser';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId;
  listingType: ApiListingApplicationListingType;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  applied: boolean;
  onApplied: (listingApplicationId: ApiListingApplicationId) => void;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
};

export function useListingApplicationSubmission({
  listingId,
  listingType,
  orgId,
  orgType,
  applied,
  onApplied,
  listingApplicationFailure,
}: Args) {
  const { user } = useUser();

  const { getInitialUserValues, submitListingApplicationAsUser } =
    useListingApplicationSubmissionAsUser({
      listingId,
      listingType,
      orgId,
      orgType,
      onApplied,
      listingApplicationFailure,
    });

  const { submitListingApplicationAsGuest } =
    useListingApplicationSubmissionAsGuest({
      listingId,
      listingType,
      applied,
      onApplied,
    });

  const submitListingApplication: FormSubmitFunction<
    ListingApplicationPayload
  > = async ({
    values,
    handleError,
    updateEdited,
    updateSubmitting,
    clearErrors,
  }) => {
    try {
      if (user) {
        await submitListingApplicationAsUser({ user, values });
      } else {
        await submitListingApplicationAsGuest({ values });
        clearErrors();
      }

      // The form can be long, and upon applying the user may be at an awkward
      // scroll position further down the page.
      //
      // To avoid that, we scroll the apply section into view so can user
      // can clearly see the "you've applied" message
      document.getElementById('apply')?.scrollIntoView();
    } catch (error) {
      handleError(error as Error);
    } finally {
      updateSubmitting(false);
      updateEdited(false);
    }
  };

  return {
    submitListingApplication,
    listingApplicationInitialValues: user
      ? listingApplicationFailure?.initialValues ||
        getInitialUserValues({ user })
      : {},
    listingApplicationInitialErrors: listingApplicationFailure?.errors ?? {},
  };
}
